import { ref, watchEffect } from 'vue'

export function useNationalIdValidator() {

    const countryCode = ref<string|null>(null);
    const nationalId = ref<string|null>(null);

    const type = ref<string|null>(null);
    const shortType = ref<string|null>(null);

    const canValidate = ref<boolean>(false);
    const isValid = ref<boolean|null>(null);

    const nationalIdTypes: Record<string, string|null> = {
        NL: "BSN",
        BE: "RRN",
        FR: "NIR",
        PL: "PESEL",
        NA: null
    };

    function update(): void {
        const types: Record<string, string|null> = {
            NL: "BSN (Burgerservicenummer)",
            BE: "RRN (Rijksregisternummer)",
            FR: "INSEE / NIR (Numéro de Sécurité Sociale)",
            PL: "PESEL (Powszechny Elektroniczny System Ewidencji Ludności)",
            NA: "ID-kaart nummer."
        };

        canValidate.value = countryCode.value !== "NA";
        type.value = types[countryCode.value ?? ""] ?? null;
        shortType.value = nationalIdTypes[countryCode.value ?? ""] ?? null;

        validate();
    }

    function validate(): void {
        const validators: Record<string, () => boolean> = {
            NL: validateNetherlands,
            BE: validateBelgium,
            FR: validateFrance,
            PL: validatePoland,
            NA: () => true
        };

        isValid.value = validators[countryCode.value ?? "NA"]?.();
    }

    function validateNetherlands(): boolean {
        if (!nationalId.value || !/^\d{9}$/.test(nationalId.value)) return false;

        let sum: number = 0;

        for (let i = 0; i < 8; i++) {
            sum += parseInt(nationalId.value[i]) * (9 - i);
        }

        sum -= parseInt(nationalId.value[8]);

        return sum % 11 === 0;
    }

    function validateBelgium(): boolean {
        if (!nationalId.value || !/^\d{11}$/.test(nationalId.value)) return false;

        const birthSequence = nationalId.value.slice(0, 9);
        const checkSum = parseInt(nationalId.value.slice(9));

        const fullNumber = parseInt(birthSequence, 10);

        const yearCheckSums: number[] = [
            97 - (fullNumber % 97),
            97 - (parseInt("2" + birthSequence) % 97)
        ];

        return checkSum === yearCheckSums[0] || checkSum === yearCheckSums[1];
    }

    function validateFrance(): boolean {
        if (!nationalId.value || !/^\d{15}$/.test(nationalId.value)) return false;

        const baseNumber = nationalId.value.slice(0, 13);
        const controlKey = parseInt(nationalId.value.slice(13));

        const numericBase = BigInt(baseNumber);
        const calculatedKey = 97n - (numericBase % 97n);

        return controlKey === Number(calculatedKey);
    }

    function validatePoland(): boolean {
        if (!nationalId.value || !/^\d{11}$/.test(nationalId.value)) return false;

        let sum = 0;
        const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];

        for (let i = 0; i < 10; i++) {
            sum += parseInt(nationalId.value[i], 10) * weights[i];
        }

        const calculated = (10 - (sum % 10)) % 10;
        const checkSum = parseInt(nationalId.value[10], 10);

        return calculated === checkSum;
    }

    watchEffect(() => update());

    return {
        type,
        shortType,
        isValid,
        nationalId,
        countryCode,
        canValidate,
        nationalIdTypes,
    };
}